/**
 * Shared walker for the HRIS form schemas.
 *
 * The employee form, the Workday hire form and both prefill modules all need
 * the same thing: visit every leaf of a nested `{ type: 'object', properties }`
 * schema and patch some of them. Written once here because the subtle part is
 * easy to get wrong three separate ways.
 *
 * **Structural sharing is the point.** A branch that nothing matched is
 * returned by identity, not cloned. FormBuilder runs with `enableReinitialize`,
 * so a schema that is a new object on every pass resets fields the recruiter
 * has already filled in. `touched` is per-recursion-level for the same reason:
 * a match in one branch must not force untouched siblings to be rebuilt.
 */

interface SchemaLeaf {
  type?: string;
  properties?: Record<string, SchemaLeaf>;
  [key: string]: unknown;
}

type Schema = Record<string, SchemaLeaf>;

/**
 * @param schema the nested schema, or undefined
 * @param patch  called per leaf; return the fields to merge, or undefined to
 *               leave that leaf exactly as it is
 */
export const mapSchemaLeaves = <T extends Schema>(
  schema: T | undefined,
  patch: (_key: string, _field: SchemaLeaf) => Partial<SchemaLeaf> | undefined
): T | undefined => {
  if (!schema) return schema;

  const decorate = (node: Schema): Schema => {
    const next: Schema = {};
    let touched = false;

    for (const [key, field] of Object.entries(node)) {
      next[key] = field;
      if (!field || typeof field !== 'object') continue;

      if (field.type === 'object' && field.properties) {
        const properties = decorate(field.properties);
        if (properties !== field.properties) {
          next[key] = { ...field, properties };
          touched = true;
        }
        continue;
      }

      const patched = patch(key, field);
      if (patched && Object.keys(patched).length) {
        next[key] = { ...field, ...patched };
        touched = true;
      }
    }

    return touched ? next : node;
  };

  return decorate(schema) as T;
};
